// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
import React, { useContext } from 'react';
import { warnOnce } from '@cloudscape-design/component-toolkit/internal';
import { InternalI18nContext } from '../context';
import { I18nFormatter } from '../utils/i18n-formatter';
import { determineAppLocale } from '../utils/locales';
import { normalizeMessages } from '../utils/messages';
/**
 * Context to send parent messages down to child I18nProviders. This isn't
 * included in the InternalI18nContext to avoid components from depending on
 * MessageFormatElement types.
 */
const I18nMessagesContext = React.createContext({});
export function LocalI18nProvider({ messages: messagesArray, locale: providedLocale, children, }) {
    if (typeof document === 'undefined' && !providedLocale) {
        warnOnce('I18nProvider', 'An explicit locale was not provided during server rendering. This can lead to a hydration mismatch on the client.');
    }
    const locale = determineAppLocale(providedLocale);
    // The provider accepts an array of configs. We merge parent messages and
    // flatten the tree early on so that accesses by key are simpler and faster.
    const parentMessages = useContext(I18nMessagesContext);
    const messages = normalizeMessages([parentMessages, ...messagesArray]);
    // The formatter is recreated on every render to ensure it has access to the
    // latest messages. This is a trade-off between performance and correctness.
    // In practice, this should only happen when the messages change, which is
    // infrequent.
    const formatter = new I18nFormatter(locale, messages);
    return (React.createElement(InternalI18nContext.Provider, { value: { locale, format: formatter.format.bind(formatter) } },
        React.createElement(I18nMessagesContext.Provider, { value: messages }, children)));
}
//# sourceMappingURL=local-provider.js.map